import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import RenderMonitor from '../components/RenderMonitor';
import { getEffectiveIsGlobal } from '../utils/adminViewMode';
import { fetchLessonCompletionsForBootcamps } from '../utils/adminAnalyticsKpis';

const DAY_MS = 24 * 60 * 60 * 1000;
const OVERVIEW_CACHE_TTL_MS = 10 * 60 * 1000;

function parseStutterDays(stutterTime) {
  const match = String(stutterTime || '').match(/(\d+(\.\d+)?)/);
  const parsed = match ? Number(match[1]) : 1;
  return parsed > 0 ? parsed : 1;
}

function getUnlockedModules(bootcamp, lessonCount, nowMs) {
  const totalLessons = Number(lessonCount) || 0;
  if (totalLessons <= 0) return 0;
  const startMs = new Date(bootcamp.start_date).getTime();
  if (!Number.isFinite(startMs) || nowMs < startMs) return 0;
  const stutterDays = parseStutterDays(bootcamp.stutter_time);
  const elapsedDays = Math.floor((nowMs - startMs) / DAY_MS);
  const unlocked = Math.floor(elapsedDays / stutterDays) + 1;
  return Math.max(0, Math.min(totalLessons, unlocked));
}

async function getTeamActivityByRegion(clientId, regionIds) {
  const ids = (regionIds || []).map(String).filter(Boolean);
  if (!clientId || ids.length === 0) return { rowsByRegion: {}, latestByRegion: {} };

  const { data: serverTimeData } = await supabase.rpc('get_server_time');
  const nowMs = new Date(serverTimeData || Date.now()).getTime();

  const { data: teamsData } = await supabase
    .from('teams')
    .select('id, name, region_id, active')
    .eq('client_id', clientId)
    .in('region_id', ids)
    .eq('active', true);

  const teams = teamsData || [];
  if (teams.length === 0) return { rowsByRegion: {}, latestByRegion: {} };

  const teamIds = teams.map((t) => t.id);
  const teamNameById = new Map(teams.map((t) => [String(t.id), t.name || `Team ${t.id}`]));
  const teamRegionById = new Map(teams.map((t) => [String(t.id), String(t.region_id)]));
  const userCountByTeam = new Map();
  if (teamIds.length > 0) {
    const { data: teamUsersData } = await supabase
      .from('team_users')
      .select('team_id, user_id')
      .in('team_id', teamIds);
    (teamUsersData || []).forEach((row) => {
      const key = String(row.team_id);
      userCountByTeam.set(key, (userCountByTeam.get(key) || 0) + 1);
    });
  }

  const { data: bootcampsData } = await supabase
    .from('bootcamps')
    .select('id, team_id, course_id, start_date, stutter_time')
    .in('team_id', teamIds);
  const bootcamps = bootcampsData || [];
  const latestBootcampIdByTeam = new Map();
  (bootcamps || []).forEach((bootcamp) => {
    const teamKey = String(bootcamp.team_id);
    const cur = latestBootcampIdByTeam.get(teamKey);
    if (!cur) {
      latestBootcampIdByTeam.set(teamKey, {
        id: bootcamp.id,
        startTs: new Date(bootcamp.start_date).getTime(),
      });
      return;
    }
    const nextTs = new Date(bootcamp.start_date).getTime();
    if (nextTs >= cur.startTs) {
      latestBootcampIdByTeam.set(teamKey, { id: bootcamp.id, startTs: nextTs });
    }
  });
  const bootcampIds = bootcamps.map((b) => b.id);

  const pointsByBootcamp = new Map();
  const reflectionsByBootcamp = new Map();
  if (bootcampIds.length > 0) {
    const { data: pointsData } = await supabase
      .from('user_points')
      .select('bootcamp_id, points')
      .in('bootcamp_id', bootcampIds);
    (pointsData || []).forEach((p) => {
      const key = String(p.bootcamp_id);
      pointsByBootcamp.set(key, (pointsByBootcamp.get(key) || 0) + (Number(p.points) || 0));
    });

    const { data: reflectionsData } = await supabase
      .from('user_reflections')
      .select('id, bootcamp_id, user_id, reflection_text, follow_up_question, follow_up_answer, submitted_at')
      .in('bootcamp_id', bootcampIds)
      .order('submitted_at', { ascending: false });
    const reflectionUserIds = Array.from(new Set((reflectionsData || []).map((r) => String(r.user_id)).filter(Boolean)));
    const userNameById = new Map();
    if (reflectionUserIds.length > 0) {
      const { data: usersData } = await supabase
        .from('users')
        .select('id, full_name, email')
        .in('id', reflectionUserIds);
      (usersData || []).forEach((u) => {
        userNameById.set(String(u.id), u.full_name || u.email || 'Unknown user');
      });
    }
    const bootcampTeamIdById = new Map(bootcamps.map((b) => [String(b.id), String(b.team_id)]));
    (reflectionsData || []).forEach((row) => {
      const key = String(row.bootcamp_id);
      if (!reflectionsByBootcamp.has(key)) reflectionsByBootcamp.set(key, []);
      const teamId = bootcampTeamIdById.get(key);
      reflectionsByBootcamp.get(key).push({
        ...row,
        authorName: userNameById.get(String(row.user_id)) || 'Unknown user',
        teamName: teamNameById.get(teamId) || 'Unknown team',
      });
    });
  }

  const lessonsByCourse = new Map();
  const courseIds = Array.from(new Set(bootcamps.map((b) => b.course_id).filter((v) => v != null)));
  if (courseIds.length > 0) {
    const { data: lessonsData } = await supabase
      .from('lessons')
      .select('course_id, lesson_type')
      .in('course_id', courseIds);
    const nonOnboarding = new Map();
    const allLessons = new Map();
    (lessonsData || []).forEach((lesson) => {
      const key = String(lesson.course_id);
      allLessons.set(key, (allLessons.get(key) || 0) + 1);
      if (lesson.lesson_type !== 'onboarding') {
        nonOnboarding.set(key, (nonOnboarding.get(key) || 0) + 1);
      }
    });
    courseIds.forEach((courseId) => {
      const key = String(courseId);
      lessonsByCourse.set(key, nonOnboarding.get(key) || allLessons.get(key) || 0);
    });
  }

  const teamNumerator = new Map(teamIds.map((teamId) => [String(teamId), 0]));
  const teamDenominator = new Map(teamIds.map((teamId) => [String(teamId), 0]));
  const teamTotalReflections = new Map(teamIds.map((teamId) => [String(teamId), 0]));
  const teamCompletionCount = new Map(teamIds.map((teamId) => [String(teamId), 0]));
  const regionLatestReflections = new Map(ids.map((regionId) => [String(regionId), []]));
  bootcamps.forEach((bootcamp) => {
    const teamKey = String(bootcamp.team_id);
    const users = userCountByTeam.get(teamKey) || 0;
    const totalLessons = lessonsByCourse.get(String(bootcamp.course_id)) || 0;
    const unlocked = getUnlockedModules(bootcamp, totalLessons, nowMs);
    const latestForTeam = latestBootcampIdByTeam.get(teamKey);
    if (latestForTeam && String(latestForTeam.id) === String(bootcamp.id)) {
      const numeratorAdd = pointsByBootcamp.get(String(bootcamp.id)) || 0;
      const denominatorAdd = users * unlocked;
      teamNumerator.set(teamKey, (teamNumerator.get(teamKey) || 0) + numeratorAdd);
      teamDenominator.set(teamKey, (teamDenominator.get(teamKey) || 0) + denominatorAdd);
    }
    const reflections = reflectionsByBootcamp.get(String(bootcamp.id)) || [];
    teamTotalReflections.set(teamKey, (teamTotalReflections.get(teamKey) || 0) + reflections.length);
    if (reflections.length > 0) {
      const regionKey = teamRegionById.get(teamKey);
      if (!regionKey) return;
      const merged = [...(regionLatestReflections.get(regionKey) || []), ...reflections]
        .sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())
        .slice(0, 3);
      regionLatestReflections.set(regionKey, merged);
    }
  });

  if (bootcampIds.length > 0) {
    const completionsData = await fetchLessonCompletionsForBootcamps(bootcampIds);
    const bootcampTeamById = new Map(bootcamps.map((b) => [String(b.id), String(b.team_id)]));
    completionsData.forEach((row) => {
      const teamKey = bootcampTeamById.get(String(row.bootcamp_id));
      if (!teamKey) return;
      teamCompletionCount.set(teamKey, (teamCompletionCount.get(teamKey) || 0) + 1);
    });
  }

  const byRegion = {};
  teams.forEach((team) => {
    const key = String(team.region_id);
    if (!byRegion[key]) byRegion[key] = [];
    const numerator = teamNumerator.get(String(team.id)) || 0;
    const denominator = teamDenominator.get(String(team.id)) || 0;
    const rawScore = denominator > 0 ? (numerator / denominator) : 0;
    byRegion[key].push({
      teamId: team.id,
      teamName: team.name || `Team ${team.id}`,
      rawScore,
      checkpointsCompleted: teamCompletionCount.get(String(team.id)) || 0,
      totalReflections: teamTotalReflections.get(String(team.id)) || 0,
    });
  });

  Object.keys(byRegion).forEach((regionId) => {
    const maxRaw = Math.max(0, ...byRegion[regionId].map((row) => row.rawScore || 0));
    byRegion[regionId] = byRegion[regionId]
      .map((row) => ({
        ...row,
        relativeScore: maxRaw > 0 ? Math.max(0, Math.min(1, row.rawScore / maxRaw)) : 0,
      }))
      .sort((a, b) => {
        if (b.relativeScore !== a.relativeScore) return b.relativeScore - a.relativeScore;
        return String(a.teamName).localeCompare(String(b.teamName));
      });
  });

  return { rowsByRegion: byRegion, latestByRegion: Object.fromEntries(regionLatestReflections.entries()) };
}

function ClientViewer() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [regions, setRegions] = useState([]);
  const [openRegions, setOpenRegions] = useState({});
  const [regionData, setRegionData] = useState({}); // regionId -> { teams, loading }
  const [openTeamHistory, setOpenTeamHistory] = useState({}); // `${regionId}:${teamId}` -> boolean
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [regionAnalytics, setRegionAnalytics] = useState({});
  const hasAutoScrolledToRegionRef = useRef(false);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        // Verify non-global admin has access to this client (via client or region)
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;
        if (!userId) { setError('Unauthorized'); setLoading(false); return; }
        const cacheKey = `clientViewer.overview.${userId}.${String(id)}.v1`;
        try {
          const raw = localStorage.getItem(cacheKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if ((Date.now() - (parsed?.savedAt || 0)) < OVERVIEW_CACHE_TTL_MS && parsed?.data) {
              setClient(parsed.data.client || { id, business_name: 'Client' });
              setRegions(parsed.data.regions || []);
              setRegionAnalytics(parsed.data.regionAnalytics || {});
              setIsGlobalAdmin(getEffectiveIsGlobal(!!parsed.data.isGlobalAdmin));
              setLoading(false);
              return;
            }
          }
        } catch {}

        const [{ data: lean }, { data: cliRows }, { data: regRows }] = await Promise.all([
          supabase.from('lean_admin_users').select('is_global').eq('user_id', userId).maybeSingle(),
          supabase.from('admin_user_clients').select('client_id').eq('user_id', userId),
          supabase.from('admin_user_regions').select('region_id').eq('user_id', userId),
        ]);
        const isGlobal = !!lean?.is_global;
        setIsGlobalAdmin(getEffectiveIsGlobal(isGlobal));
        if (isGlobal) {
          // Global admins should use the full ClientDetail; but if they landed here, allow read-only view
        }

        // Get client; if blocked by RLS, fallback to generic
        const { data: clientRow } = await supabase
          .from('clients')
          .select('id, business_name')
          .eq('id', id)
          .maybeSingle();
        if (clientRow) setClient(clientRow); else setClient({ id, business_name: 'Client' });

        // Determine region access for this client
        let allowedRegionIds = new Set();
        // If client-level manager
        const clientIds = new Set((cliRows || []).map(r => String(r.client_id)));
        const hasClientScope = clientIds.has(String(id));
        if (!hasClientScope) {
          // Region-level: fetch regions mapping and restrict to those with access
          const regionIds = (regRows || []).map(r => r.region_id);
          if (regionIds.length > 0) {
            const { data: regionMap } = await supabase
              .from('regions')
              .select('id, client_id')
              .in('id', regionIds);
            (regionMap || []).forEach(r => {
              if (String(r.client_id) === String(id)) allowedRegionIds.add(r.id);
            });
          }
          if (allowedRegionIds.size === 0 && !hasClientScope) {
            setError('You do not have access to this client');
            setLoading(false);
            return;
          }
        }

        // Fetch regions for this client (active only)
        const { data: regionsData } = await supabase
          .from('regions')
          .select('id, name, active')
          .eq('client_id', id)
          .eq('active', true);
        const visibleRegions = (regionsData || []).filter(r => hasClientScope || allowedRegionIds.has(r.id));

        // Initialize regions (teams lazy-loaded)
        const regionList = visibleRegions.map(r => ({ id: r.id, name: r.name }));
        setRegions(regionList);
        const analytics = await getTeamActivityByRegion(id, regionList.map((r) => r.id));
        setRegionAnalytics(analytics.rowsByRegion || {});
        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              savedAt: Date.now(),
              data: {
                client: clientRow || { id, business_name: 'Client' },
                regions: regionList,
                regionAnalytics: analytics.rowsByRegion || {},
                isGlobalAdmin: isGlobal,
              },
            })
          );
        } catch {}
        setLoading(false);
      } catch (e) {
        setError('Unexpected error');
        setLoading(false);
      }
    };
    run();
  }, [id]);

  useEffect(() => {
    hasAutoScrolledToRegionRef.current = false;
  }, [id, location.state?.targetRegionId]);

  useEffect(() => {
    const targetRegionId = location.state?.targetRegionId;
    if (loading || !targetRegionId || hasAutoScrolledToRegionRef.current) return;

    let attempts = 0;
    const maxAttempts = 10;
    let timeoutId;

    const tryScroll = () => {
      const targetEl = document.getElementById(`region-${String(targetRegionId)}`);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        hasAutoScrolledToRegionRef.current = true;
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        timeoutId = setTimeout(tryScroll, 120);
      }
    };

    timeoutId = setTimeout(tryScroll, 80);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loading, regions, location.state?.targetRegionId]);

  return (
    <div className="modern-container-narrow">
      <RenderMonitor componentName="ClientViewer" />
      <div className="modern-card">
        <div className="modern-flex modern-justify-between modern-items-center">
          <h1 className="modern-title">{client?.business_name || 'Client'}</h1>
          {!isGlobalAdmin && (
            <div className="modern-flex modern-items-center" style={{ gap: 10 }}>
              <button
                className="modern-button modern-button-secondary"
                onClick={() => navigate('/manager')}
              >
                ← All Clients
              </button>
              <button
                className="modern-button modern-button-secondary"
                onClick={async () => { try { await supabase.auth.signOut(); } catch {} navigate('/login', { replace: true }); }}
              >
                Logout
              </button>
            </div>
          )}
        </div>
        {loading ? (
          <div className="modern-loading">Loading…</div>
        ) : error ? (
          <div className="modern-error">{error}</div>
        ) : (
          <div style={{ marginTop: 12 }}>
            {regions.length === 0 ? (
              <div style={{ color: '#64748b' }}>No visible regions.</div>
            ) : (
              regions.map(region => (
                <div id={`region-${String(region.id)}`} key={String(region.id)} className="modern-card" style={{ marginBottom: 12 }}>
                  <div className="modern-flex modern-justify-between modern-items-center modern-mb-2">
                    <h3 className="modern-heading">{region.name}</h3>
                    <button
                      className="modern-button modern-button-secondary"
                      onClick={async () => {
                        const isOpen = !!openRegions[String(region.id)];
                        setOpenRegions(prev => ({ ...prev, [String(region.id)]: !isOpen }));
                        if (!isOpen && !regionData[String(region.id)]) {
                          // Load teams for this region and cache in memory
                          setRegionData(prev => ({ ...prev, [String(region.id)]: { teams: [], loading: true } }));
                          // Fetch teams for region
                          const { data: teamsData } = await supabase
                            .from('teams')
                            .select('id, name, region_id, active')
                            .eq('client_id', id)
                            .eq('region_id', region.id)
                            .eq('active', true);
                          const teams = (teamsData || []).map(t => ({
                            ...t,
                            users: [],
                            activeBootcamp: null,
                            upcomingBootcamp: null,
                            latestCompletedBootcamp: null,
                            bootcamps: [],
                          }));
                          // Users for this region's teams
                          const teamIds = teams.map(t => t.id);
                          let usersById = {};
                          if (teamIds.length > 0) {
                            const { data: teamUsers } = await supabase
                              .from('team_users')
                              .select('team_id, user_id')
                              .in('team_id', teamIds);
                            const userIdSet = new Set((teamUsers || []).map(tu => String(tu.user_id)));
                            if (userIdSet.size > 0) {
                              const { data: usersData } = await supabase
                                .from('users')
                                .select('id, full_name, email')
                                .in('id', Array.from(userIdSet));
                              (usersData || []).forEach(u => { usersById[String(u.id)] = { id: String(u.id), full_name: u.full_name, email: u.email }; });
                            }
                            (teamUsers || []).forEach(tu => {
                              const idx = teams.findIndex(t => t.id === tu.team_id);
                              if (idx >= 0) {
                                const u = usersById[String(tu.user_id)];
                                if (u) (teams[idx].users || (teams[idx].users = [])).push(u);
                              }
                            });
                          }
                          // Bootcamps for these teams (active/upcoming/completed)
                          if (teamIds.length > 0) {
                            const now = Date.now();
                            const { data: bootcamps } = await supabase
                              .from('bootcamps')
                              .select('id, start_date, end_date, team_id, courses(name), teams(name)')
                              .in('team_id', teamIds);

                            (bootcamps || []).forEach(bc => {
                              const idx = teams.findIndex(t => t.id === bc.team_id);
                              if (idx < 0) return;
                              (teams[idx].bootcamps || (teams[idx].bootcamps = [])).push(bc);
                              const startTs = new Date(bc.start_date).getTime();
                              const endTs = new Date(bc.end_date).getTime();
                              if (startTs <= now && endTs > now) {
                                teams[idx].activeBootcamp = bc;
                              } else if (startTs > now) {
                                // choose soonest upcoming
                                const cur = teams[idx].upcomingBootcamp;
                                if (!cur || new Date(cur.start_date).getTime() > startTs) teams[idx].upcomingBootcamp = bc;
                              } else if (endTs <= now) {
                                // choose most recent completed
                                const cur = teams[idx].latestCompletedBootcamp;
                                if (!cur || new Date(cur.end_date).getTime() < endTs) teams[idx].latestCompletedBootcamp = bc;
                              }
                            });

                            // Sort bootcamp history newest-first by start date
                            teams.forEach(t => {
                              t.bootcamps = (t.bootcamps || []).slice().sort((a, b) => {
                                const as = new Date(a.start_date).getTime();
                                const bs = new Date(b.start_date).getTime();
                                return bs - as;
                              });
                            });
                          }
                          setRegionData(prev => ({ ...prev, [String(region.id)]: { teams, loading: false } }));
                        }
                      }}
                    >
                      {openRegions[String(region.id)] ? 'Hide Per Team Data' : 'Per Team Data'}
                    </button>
                  </div>
                  {(regionAnalytics[String(region.id)] || []).length !== 1 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6 }}>Activity Levels per Team</div>
                      {(regionAnalytics[String(region.id)] || []).length === 0 ? (
                        <div style={{ color: '#94a3b8' }}>No active teams yet.</div>
                      ) : (
                        <div className="modern-flex modern-flex-col modern-gap-2">
                          {(regionAnalytics[String(region.id)] || []).map((row) => (
                            <div key={`${region.id}-${row.teamId}`}>
                              <div className="modern-flex modern-justify-between modern-items-center" style={{ flexWrap: 'nowrap' }}>
                                <span className="modern-flex modern-items-center" style={{ color: '#475569', gap: 8, whiteSpace: 'nowrap' }}>
                                  <span
                                    style={{
                                      width: 12,
                                      height: 12,
                                      backgroundColor: '#FB7820',
                                      opacity: Math.max(0.12, row.relativeScore || 0),
                                      borderRadius: 2,
                                      display: 'inline-block',
                                    }}
                                  />
                                  {row.teamName}
                                  <span style={{ color: '#64748b', fontSize: 13 }}>{row.totalReflections} reflections</span>
                                  <span style={{ color: '#64748b', fontSize: 13 }}>{row.unlockedModules}/{row.totalModules} modules unlocked</span>
                                </span>
                                <span style={{ fontWeight: 600 }}>{(row.relativeScore || 0).toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {openRegions[String(region.id)] && (
                    (
                      regionData[String(region.id)]?.loading ? (
                        <div className="modern-loading">Loading…</div>
                      ) : (
                        ((regionData[String(region.id)]?.teams || []).length === 0) ? (
                          <div style={{ color: '#94a3b8' }}>No teams in this region.</div>
                        ) : (
                          (regionData[String(region.id)]?.teams || []).map(team => (
                            <div key={team.id} className="modern-card" style={{ marginBottom: 10 }}>
                              <div className="modern-flex modern-justify-between modern-items-center modern-mb-2">
                                <h4 className="modern-heading">{team.name}</h4>
                                <button
                                  className="modern-button modern-button-secondary"
                                  onClick={() => {
                                    const key = `${String(region.id)}:${String(team.id)}`;
                                    setOpenTeamHistory(prev => ({ ...prev, [key]: !prev[key] }));
                                  }}
                                >
                                  {openTeamHistory[`${String(region.id)}:${String(team.id)}`] ? 'Hide History' : 'Bootcamp History'}
                                </button>
                              </div>
                              <div className="modern-flex modern-flex-wrap modern-gap-2 modern-mb-2">
                                {(team.users || []).length === 0 ? (
                                  <span style={{ color: '#94a3b8' }}>No users in this team.</span>
                                ) : (
                                  (team.users || []).map(u => (
                                    <div key={u.id} className="modern-user-tag" title={u.email || ''}>
                                      {u.full_name ? u.full_name : u.email}
                                    </div>
                                  ))
                                )}
                              </div>
                              {team.activeBootcamp ? (
                                <button
                                  className="modern-button"
                                  style={{
                                    marginTop: 16,
                                    background: 'linear-gradient(90deg, #10b981 0%, #06b6d4 100%)',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '10px 18px',
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    boxShadow: '0 8px 16px rgba(16,185,129,0.25)'
                                  }}
                                  onClick={() => navigate(`/bootcamp/${team.activeBootcamp.id}`)}
                                >
                                  View Active Bootcamp →
                                </button>
                              ) : team.upcomingBootcamp ? (
                                <button
                                  className="modern-button modern-button-info"
                                  style={{ marginTop: 16 }}
                                  onClick={() => navigate(`/bootcamp/${team.upcomingBootcamp.id}`)}
                                >
                                  View Upcoming Bootcamp →
                                </button>
                              ) : team.latestCompletedBootcamp ? (
                                <button
                                  className="modern-button modern-button-warning"
                                  style={{ marginTop: 16 }}
                                  onClick={() => navigate(`/bootcamp/${team.latestCompletedBootcamp.id}`)}
                                >
                                  View Latest Bootcamp →
                                </button>
                              ) : null}

                              {openTeamHistory[`${String(region.id)}:${String(team.id)}`] ? (
                                <div style={{ marginTop: 14 }}>
                                  {(team.bootcamps || []).length === 0 ? (
                                    <div style={{ color: '#94a3b8' }}>No bootcamps for this team.</div>
                                  ) : (
                                    <div className="modern-flex modern-flex-col modern-gap-2">
                                      {(team.bootcamps || []).map((bc) => {
                                        const now = Date.now();
                                        const startTs = new Date(bc.start_date).getTime();
                                        const endTs = new Date(bc.end_date).getTime();
                                        const status = startTs > now ? 'Upcoming' : (endTs <= now ? 'Completed' : 'Active');
                                        const badgeClass = status === 'Active'
                                          ? 'modern-badge-info'
                                          : (status === 'Upcoming' ? 'modern-badge-warning' : 'modern-badge-success');

                                        return (
                                          <div key={bc.id} className="modern-card" style={{ padding: 14 }}>
                                            <div className="modern-flex modern-justify-between modern-items-center">
                                              <div>
                                                <div style={{ fontWeight: 700 }}>{bc.courses?.name || 'Bootcamp'}</div>
                                                <div style={{ color: '#64748b', fontSize: 13 }}>
                                                  {new Date(bc.start_date).toLocaleDateString()} – {new Date(bc.end_date).toLocaleDateString()}
                                                </div>
                                              </div>
                                              <div className="modern-flex modern-items-center" style={{ gap: 10 }}>
                                                <span className={`modern-badge ${badgeClass}`}>{status}</span>
                                                <button
                                                  className="modern-button modern-button-info"
                                                  onClick={() => navigate(`/bootcamp/${bc.id}`)}
                                                >
                                                  View →
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          ))
                        )
                      )
                    )
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export { getTeamActivityByRegion };

export default ClientViewer;


